"use client"

import { cn } from "@/lib/utils"
import type { BacktestFinalStatus, BacktestJob, BacktestResult } from "@/types/backtest"

const CONDITION_ORDER = ["유동주식수", "일봉주봉", "믹스매매"]

const STATUS_BADGE: Record<BacktestFinalStatus, string> = {
  "성공":       "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
  "기간초과":   "bg-yellow-500/20  text-yellow-400  border-yellow-500/40",
  "매수없음":   "bg-zinc-700/60    text-zinc-400    border-zinc-600",
  "매수전상승": "bg-blue-500/20    text-blue-400    border-blue-500/40",
  "진행중":     "bg-purple-500/20  text-purple-400  border-purple-500/40",
}

function fmtPct(n: number): string {
  return `${n > 0 ? "+" : ""}${n.toFixed(2)}%`
}

function returnColor(n: number) {
  return n > 0 ? "text-red-400" : n < 0 ? "text-blue-400" : "text-zinc-400"
}

interface Props {
  job: BacktestJob | null
}

export function BacktestResultTab({ job }: Props) {
  if (!job) {
    return (
      <div className="flex h-24 items-center justify-center text-xs text-zinc-500">
        아직 실행된 백테스트가 없습니다.
      </div>
    )
  }

  if (job.status === "running") {
    const pct = job.totalCount > 0 ? (job.processedCount / job.totalCount) * 100 : 0
    return (
      <div className="flex h-24 flex-col items-center justify-center gap-2 text-xs text-zinc-500">
        <span>백테스트 실행 중... ({job.processedCount}/{job.totalCount} 조건식 완료)</span>
        <div className="h-1.5 w-40 overflow-hidden rounded-full bg-zinc-800">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>
    )
  }

  if (job.status === "failed") {
    return (
      <div className="flex h-24 items-center justify-center text-xs text-red-400">
        백테스트 실행 실패: {job.message ?? "알 수 없는 오류"}
      </div>
    )
  }

  const sortedResults = [...job.results].sort(
    (a, b) => CONDITION_ORDER.indexOf(a.conditionName) - CONDITION_ORDER.indexOf(b.conditionName)
  )

  return (
    <div className="space-y-6 p-4">
      {sortedResults.map((result) => (
        <ConditionSection key={result.conditionName} result={result} />
      ))}
    </div>
  )
}

function ConditionSection({ result }: { result: BacktestResult }) {
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        <h3 className="font-semibold text-zinc-100">{result.conditionName}</h3>
        <span className="text-xs text-zinc-500">
          승률 <span className="font-mono text-zinc-200">{result.winRate.toFixed(1)}%</span>
          {" "}({result.successCount}/{result.totalCount})
        </span>
        <span className="text-xs text-zinc-500">
          평균수익률{" "}
          <span className={cn("font-mono", returnColor(result.avgReturn))}>{fmtPct(result.avgReturn)}</span>
        </span>
      </div>

      {result.results.length === 0 ? (
        <div className="flex h-16 items-center justify-center text-xs text-zinc-500">
          대상 종목이 없습니다.
        </div>
      ) : (
        <div className="overflow-x-auto rounded border border-zinc-800">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-zinc-800 bg-zinc-900/60">
                <th className="px-3 py-2 text-left font-medium text-zinc-500">종목명</th>
                <th className="px-3 py-2 text-left font-medium text-zinc-500">검색날짜</th>
                <th className="px-3 py-2 text-left font-medium text-zinc-500">매수/매도 내역</th>
                <th className="px-3 py-2 text-right font-medium text-zinc-500">수익률</th>
                <th className="px-3 py-2 text-center font-medium text-zinc-500">결과</th>
              </tr>
            </thead>
            <tbody>
              {result.results.map((stock) => (
                <tr
                  key={`${stock.stockCode}-${stock.searchedDate}`}
                  className="border-b border-zinc-800/50 hover:bg-zinc-800/30"
                >
                  <td className="px-3 py-1.5">
                    <div className="text-zinc-200">{stock.stockName}</div>
                    <div className="font-mono text-[10px] text-zinc-500">{stock.stockCode}</div>
                  </td>
                  <td className="px-3 py-1.5 text-zinc-400">{stock.searchedDate}</td>
                  <td className="px-3 py-1.5">
                    {stock.trades.length === 0 ? (
                      <span className="text-zinc-600">-</span>
                    ) : (
                      <div className="flex flex-col gap-0.5">
                        {stock.trades.map((t, i) => (
                          <span
                            key={i}
                            className={cn("font-mono", t.tradeType === "buy" ? "text-red-400" : "text-blue-400")}
                          >
                            {t.tradeType === "buy" ? "매수" : "매도"} {t.tradeDate} {t.price.toLocaleString("ko-KR")}원 x{t.quantity}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className={cn("px-3 py-1.5 text-right font-mono", returnColor(stock.totalReturn))}>
                    {fmtPct(stock.totalReturn)}
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    <span className={cn("rounded border px-1.5 py-0.5 text-[10px]", STATUS_BADGE[stock.finalStatus])}>
                      {stock.finalStatus}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
